import axios from 'axios';

// NoCodeBackend API configuration
const API_BASE_URL = import.meta.env.VITE_NOCODEBACKEND_URL || 'https://openapi.nocodebackend.com';
const API_KEY = import.meta.env.VITE_NOCODEBACKEND_KEY || '';
const INSTANCE_ID = import.meta.env.VITE_NOCODEBACKEND_INSTANCE || '54230_nocodebackend';

// Validate required environment variables
if (!API_KEY) {
  console.warn('NoCodeBackend API key is not configured. Please set VITE_NOCODEBACKEND_KEY environment variable.');
}

const ncbApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // Increased timeout to 15 seconds
});

// Helper functions for error handling
function extractErrorMessage(error) {
  if (!error) return 'Unknown error';

  const res = error.response;

  if (typeof res?.data === 'string') return res.data;

  if (res?.data?.error) return res.data.error;
  if (res?.data?.message) return res.data.message;

  if (Array.isArray(res?.data?.errors) && res.data.errors.length > 0) {
    return res.data.errors.join(', ');
  }

  if (res?.status) {
    return `Request failed with status ${res.status}`;
  }

  return error.message || 'Unknown error';
}

function recordApiError(context, error) {
  try {
    const existing =
      JSON.parse(localStorage.getItem('shophub_api_errors') || '[]') || [];
    const entry = {
      time: new Date().toISOString(),
      context,
      status: error?.response?.status || null,
      url: error?.config?.url || null,
      method: error?.config?.method || null,
      response: error?.response?.data || null,
      message: extractErrorMessage(error),
    };
    const updated = [entry, ...existing].slice(0, 20);
    localStorage.setItem('shophub_api_errors', JSON.stringify(updated));
  } catch (e) {
    // fail silently
  }
}

// Request interceptor for auth token and instance parameter
ncbApi.interceptors.request.use(
  (config) => {
    // Add Authorization header with API key
    if (API_KEY) {
      config.headers.Authorization = `Bearer ${API_KEY}`;
    }
    
    // Add Instance parameter to all requests
    const url = new URL(config.url, API_BASE_URL);
    url.searchParams.set('Instance', INSTANCE_ID);
    config.url = url.toString();
    
    console.log(`NCB Request: ${config.method?.toUpperCase()} ${config.url}`, {
      headers: config.headers,
      data: config.data
    });
    
    return config;
  },
  (error) => {
    console.error('NCB Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
ncbApi.interceptors.response.use(
  (response) => {
    console.log(`NCB Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('NCB API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url
      }
    });
    
    if (error.response?.status === 401) {
      localStorage.removeItem('auth-token');
      localStorage.removeItem('user-data');
      window.location.href = '/#/login';
    }
    
    return Promise.reject(error);
  }
);

// Helper function to build query parameters
const buildQueryParams = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });
  return queryParams.toString();
};

// Product CRUD functions with proper error handling
async function createProduct(body) {
  const url = `/create/products?Instance=${INSTANCE_ID}`;

  try {
    const response = await ncbApi.post(url, body);
    return response.data;
  } catch (error) {
    recordApiError('createProduct', error);
    throw new Error(extractErrorMessage(error));
  }
}

async function updateProduct(id, body) {
  const url = `/update/products/${id}?Instance=${INSTANCE_ID}`;

  try {
    const response = await ncbApi.put(url, body);
    return response.data;
  } catch (error) {
    recordApiError('updateProduct', error);
    throw new Error(extractErrorMessage(error));
  }
}

// NCB Client Functions
export const ncbClient = {
  // Health check
  async healthCheck() {
    try {
      const url = `/read/products?Instance=${INSTANCE_ID}&limit=1`;
      console.log(`NCB Health Check URL: ${API_BASE_URL}${url}`);
      const response = await ncbApi.get(url);
      console.log(`NCB Health Check Status: ${response.status}`);
      return { status: response.status, ok: response.status === 200 };
    } catch (error) {
      console.error(`NCB Health Check Failed - Status: ${error.response?.status || 'N/A'}, Error: ${error.message.substring(0, 100)}`);
      return { status: error.response?.status || 'ERROR', ok: false, error: error.message };
    }
  },

  // Products
  async readProducts(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/read/products?${queryParams}`;
    const response = await ncbApi.get(url);
    return response.data;
  },

  async readProduct(id) {
    const response = await ncbApi.get(`/read/products/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  createProduct,
  updateProduct,

  async deleteProduct(id) {
    const response = await ncbApi.delete(`/delete/products/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  // Categories
  async readCategories() {
    const response = await ncbApi.get(`/read/categories?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  // Users
  async searchUsers(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/search/users?${queryParams}`;
    const response = await ncbApi.post(url, params);
    return response.data;
  },

  async createUser(userData) {
    const response = await ncbApi.post('/create/users', userData);
    return response.data;
  },

  async updateUser(id, userData) {
    const response = await ncbApi.put(`/update/users/${id}`, userData);
    return response.data;
  },

  // Cart
  async readCart(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/read/cart?${queryParams}`;
    const response = await ncbApi.get(url);
    return response.data;
  },

  async createCartItem(cartItemData) {
    const response = await ncbApi.post('/create/cart', cartItemData);
    return response.data;
  },

  async updateCartItem(id, cartItemData) {
    const response = await ncbApi.put(`/update/cart/${id}`, cartItemData);
    return response.data;
  },

  async deleteCartItem(id) {
    const response = await ncbApi.delete(`/delete/cart/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  // Orders
  async readOrders(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/read/orders?${queryParams}`;
    const response = await ncbApi.get(url);
    return response.data;
  },

  async readOrder(id) {
    const response = await ncbApi.get(`/read/orders/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  async createOrder(orderData) {
    const response = await ncbApi.post('/create/orders', orderData);
    return response.data;
  },

  async updateOrder(id, orderData) {
    const response = await ncbApi.put(`/update/orders/${id}`, orderData);
    return response.data;
  },

  // Payments
  async createPayment(paymentData) {
    const response = await ncbApi.post('/create/payments', paymentData);
    return response.data;
  },

  async updatePayment(id, paymentData) {
    const response = await ncbApi.put(`/update/payments/${id}`, paymentData);
    return response.data;
  },

  // Reviews
  async readReviews(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/read/reviews?${queryParams}`;
    const response = await ncbApi.get(url);
    return response.data;
  },

  async createReview(reviewData) {
    const response = await ncbApi.post('/create/reviews', reviewData);
    return response.data;
  },

  async updateReview(id, reviewData) {
    const response = await ncbApi.put(`/update/reviews/${id}`, reviewData);
    return response.data;
  },

  async deleteReview(id) {
    const response = await ncbApi.delete(`/delete/reviews/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  // Wishlist
  async readWishlist(params = {}) {
    const queryParams = buildQueryParams({ ...params, Instance: INSTANCE_ID });
    const url = `/read/wishlist?${queryParams}`;
    const response = await ncbApi.get(url);
    return response.data;
  },

  async createWishlistItem(wishlistData) {
    const response = await ncbApi.post('/create/wishlist', wishlistData);
    return response.data;
  },

  async deleteWishlistItem(id) {
    const response = await ncbApi.delete(`/delete/wishlist/${id}?Instance=${INSTANCE_ID}`);
    return response.data;
  },

  // Shipping
  async createShipping(shippingData) {
    const response = await ncbApi.post('/create/shipping', shippingData);
    return response.data;
  },
};